import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import { authFetch } from '../lib/authFetch';
import PageHeader from '../components/PageHeader';
import { PROGRAMME_WEEK_COUNT } from '../lib/weekLabels';
import useTheme4A from '../components/useTheme4A';

const AXIOM_LOGO = 'https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png';  // eslint-disable-line no-unused-vars

function JourneyPage() {
  useTheme4A();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [weekHistory, setWeekHistory] = useState([]);

  useEffect(() => {
    fetchJourneyData();
  }, []);

  const fetchJourneyData = async () => {
    // Fetch completed weeks and materials
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/journey/history`);
      if (response.ok) {
        const data = await response.json();
        setWeekHistory(data.weeks || []);
      }
    } catch (error) {
      console.error('Failed to fetch journey data:', error);
    }
  };

  // Generate weeks from -2 to current week
  // user.current_week uses the BACKEND convention: -2/-1/0 = 2 Week Window,
  // 1..30 = Programme Weeks.
  // Item #10 (ARP Repair List): the negative internal numbers are opaque to
  // users, so we render descriptive day-ranges for the 2WW rows.
  const currentInternal = user?.current_week ?? -2;
  const weeks = [];
  for (let i = -2; i <= currentInternal; i++) {
    const label = i === -2
      ? '2 Week Window · Days 1–7'
      : i === -1
        ? '2 Week Window · Days 8–14'
        : i === 0
          ? '2 Week Window Complete'
          : `Programme Week ${i} of ${PROGRAMME_WEEK_COUNT}`;
    weeks.push({
      week_number: i,
      label,
      completed: i < currentInternal,
    });
  }

  const progressLabel = currentInternal < 0
    ? '2 Week Window'
    : currentInternal === 0
      ? '2 Week Window Complete'
      : `Programme Week ${currentInternal} of ${PROGRAMME_WEEK_COUNT}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-zinc-50 to-blue-100 pb-20">
      <PageHeader title="Your Journey" />

      <main className="pt-20 px-4 pb-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold text-zinc-950 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Your Journey</h1>
          <p className="text-sm text-blue-600 mb-8">{progressLabel}</p>

          {/* Drug Screens tab hidden Feb 2026 until a real pathology partner
              is connected (ARP brief).  Journey now shows only Week History. */}
          <div className="space-y-3">
            <p className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-4">Learning Materials & Progress</p>
              
              {weeks.map((week) => (
                <div
                  key={week.week_number}
                  className={`bg-white border-2 rounded-md p-4 ${
                    week.completed ? 'border-emerald-400 bg-emerald-50' : 'border-blue-200'
                  }`}
                  data-testid={`week-${week.week_number}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className={`rounded-full p-2 ${
                        week.completed ? 'bg-emerald-500' : 'bg-blue-100'
                      }`}>
                        <BookOpen className={`w-5 h-5 ${
                          week.completed ? 'text-white' : 'text-blue-600'
                        }`} />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-zinc-900">{week.label}</h3>
                        <p className="text-xs text-blue-600">
                          {week.completed
                            ? 'Completed'
                            : week.week_number === currentInternal
                              ? 'In Progress'
                              : 'Upcoming'}
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => {
                        // 2WW = absolute Weeks 1 + 2 (intro-2ww-w<N>).
                        // Programme weeks start at 3 (week-<N>).  Anything
                        // <= 0 is the user's pre-window state and has no
                        // viewable reading yet.
                        const n = week.week_number;
                        if (n === 1 || n === 2) {
                          navigate(`/readings/intro-2ww-w${n}`);
                        } else if (n >= 3) {
                          navigate(`/readings/week-${n}`);
                        } else {
                          // Pre-window state -- send to the library so the
                          // user can read the 2WW intro readings instead.
                          navigate('/library?tab=programme');
                        }
                      }}
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      data-testid={`view-week-${week.week_number}`}
                    >
                      View Materials
                    </Button>
                  </div>
                  
                  {week.completed && (
                    <div className="mt-3 pt-3 border-t border-emerald-200">
                      <p className="text-xs text-emerald-700 font-medium">
                        ✓ Tasks completed • Materials reviewed
                      </p>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default JourneyPage;